import {useQuery} from '@tanstack/react-query';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {QUERY_KEYS} from '../../../utils/queryKeys';
import {Role, User} from '../../../incase-domain/user';
import {fetchDefaultUserConfigValues} from '../../../service/authorization';
import LoadingSpinnerComponent from '../../components/LoadingSpinnerComponent';
import FormSelect from './components/FormSelect';
import FormInput from './components/FormInput';
import ChecklistTile from '../../../incase-domain/components/checkbox/ChecklistTile';

type CreateUserType = {
    roles: Role[];
    username: string;
    email: string;
    password: string;
    passwordExpirationTime: number;
};

export enum UserFormType {
    REGISTER = 'register',
    UPDATE = 'update',
}

export type UserConfigType = {
    maxNumberOfPresentations?: number;
    maxNumberOfRooms?: number;
    maxPresentationSize?: number;
    maxNumberOfUsers?: number;
    maxGptUsage?: number;
};

export const MAX_PASSWORD_EXPIRATION_TIME = 99999;

function isCreateUserType(user: User | CreateUserType): user is CreateUserType {
    return (user as CreateUserType).password !== undefined;
}

interface UserFormProps<T extends CreateUserType | User> {
    id?: string;
    type: UserFormType;
    user: T;
    setUser: (user: T) => void;
    userConfigState: UserConfigType;
    setUserConfigState: (state: UserConfigType) => void;
    errors: Record<string, string[]>;
    loggedInUser?: User;
    spinner?: boolean;
    onSubmit: () => void;
}

export function UserForm<T extends CreateUserType | User>({
    id,
    type,
    user,
    setUser,
    userConfigState,
    setUserConfigState,
    errors,
    loggedInUser,
    spinner,
    onSubmit,
}: UserFormProps<T>): JSX.Element {
    const {t} = useTranslation();

    const adminRole = {
        label: `${t('registerPage.roleOptions.admin')}`,
        value: [Role.ADMIN],
    };

    const creatorRole = {
        label: `${t('registerPage.roleOptions.creator')}`,
        value: [Role.CREATOR],
    };

    const presenterRole = {
        label: `${t('registerPage.roleOptions.presenter')}`,
        value: [Role.PRESENTER],
    };

    const viewerRole = {
        label: `${t('registerPage.roleOptions.viewer')}`,
        value: [Role.VIEWER],
    };

    let rolesUserCanCreate: {label: string; value: Role[]}[] = [];
    if (loggedInUser?.roles?.includes(Role.SUPERUSER)) {
        rolesUserCanCreate = [adminRole, creatorRole, presenterRole, viewerRole];
    } else if (loggedInUser?.roles?.includes(Role.ADMIN)) {
        rolesUserCanCreate = [creatorRole, presenterRole, viewerRole];
    }

    const {data: defaultUserConfig} = useQuery(QUERY_KEYS.user.defaultConfig(), () => fetchDefaultUserConfigValues());

    const handleUserChange = (name: string, value: Role[] | string | string[] | number | undefined): void => {
        setUser({
            ...user,
            [name]: value,
        });
    };

    const handleUserConfigChange = (name: string, value: number | undefined): void => {
        setUserConfigState({
            ...userConfigState,
            [name]: value,
        });
    };

    const getRoleValue = (): {label: string; value: Role[]} => {
        if (user.roles && user.roles.includes(Role.ADMIN)) {
            return {label: `${t('registerPage.roleOptions.admin')}`, value: [Role.ADMIN]};
        }
        if (user.roles && user.roles.includes(Role.PRESENTER)) {
            return {label: `${t('registerPage.roleOptions.presenter')}`, value: [Role.PRESENTER]};
        }
        if (user.roles && user.roles.includes(Role.CREATOR)) {
            return {label: `${t('registerPage.roleOptions.creator')}`, value: [Role.CREATOR]};
        }
        return {label: `${t('registerPage.roleOptions.viewer')}`, value: [Role.VIEWER]};
    };

    return (
        <div>
            {spinner ? (
                <div className="c-loader__wrapper" style={{height: '50dvh'}}>
                    <LoadingSpinnerComponent />
                </div>
            ) : (
                <form
                    id={id}
                    className="c-register__form"
                    onSubmit={(e): void => {
                        e.preventDefault();
                        onSubmit();
                    }}
                >
                    {rolesUserCanCreate && (
                        <FormSelect
                            name="roles"
                            label={t('registerPage.role')}
                            options={rolesUserCanCreate}
                            value={getRoleValue()}
                            onChange={handleUserChange}
                            errors={errors.roles}
                        />
                    )}

                    <FormInput
                        type="text"
                        label={t('registerPage.username')}
                        name="username"
                        value={user.username}
                        onChange={handleUserChange}
                        errors={errors.username}
                    />

                    <FormInput
                        type="text"
                        label={t('registerPage.email')}
                        name="email"
                        value={user.email}
                        onChange={handleUserChange}
                        errors={errors.email}
                    />

                    {type === 'register' && isCreateUserType(user) && (
                        <>
                            <FormInput
                                type="password"
                                label={t('registerPage.password')}
                                name="password"
                                value={user.password}
                                onChange={handleUserChange}
                                errors={errors.password}
                            />
                        </>
                    )}

                    <FormInput
                        type="number"
                        label={t('registerPage.passwordExpirationTime')}
                        name="passwordExpirationTime"
                        value={user.passwordExpirationTime === MAX_PASSWORD_EXPIRATION_TIME ? 0 : user.passwordExpirationTime}
                        onChange={handleUserChange}
                        errors={errors.passwordExpirationTime}
                        min={1}
                        disabled={user.passwordExpirationTime === MAX_PASSWORD_EXPIRATION_TIME}
                    />

                    <div className="u-mt-1 u-mb-1">
                        <ChecklistTile
                            value={user.passwordExpirationTime === MAX_PASSWORD_EXPIRATION_TIME}
                            label="No expiration date"
                            help="registerPage.noPasswordExpirationHelp"
                            onChange={(value) => handleUserChange('passwordExpirationTime', value ? MAX_PASSWORD_EXPIRATION_TIME : 1)}
                        />
                    </div>

                    {loggedInUser?.roles?.includes(Role.SUPERUSER) &&
                        !user.roles?.includes(Role.PRESENTER) &&
                        !user.roles?.includes(Role.VIEWER) && (
                            <>
                                <FormInput
                                    type="number"
                                    label={t('registerPage.maxNumberOfPresentations')}
                                    name="maxNumberOfPresentations"
                                    value={userConfigState.maxNumberOfPresentations || defaultUserConfig?.maxNumberOfPresentations}
                                    onChange={handleUserConfigChange}
                                    errors={errors.maxNumberOfPresentations}
                                    min={1}
                                />

                                <FormInput
                                    type="number"
                                    label={t('registerPage.maxNumberOfRooms')}
                                    name="maxNumberOfRooms"
                                    value={userConfigState.maxNumberOfRooms || defaultUserConfig?.maxNumberOfRooms}
                                    onChange={handleUserConfigChange}
                                    errors={errors.maxNumberOfRooms}
                                    min={1}
                                />

                                <FormInput
                                    type="number"
                                    label={t('registerPage.maxPresentationSize')}
                                    name="maxPresentationSize"
                                    value={userConfigState.maxPresentationSize || defaultUserConfig?.maxPresentationSize}
                                    onChange={handleUserConfigChange}
                                    errors={errors.maxPresentationSize}
                                    min={1}
                                />

                                {user?.roles?.includes(Role.ADMIN) && (
                                    <>
                                        <FormInput
                                            type="number"
                                            label={t('registerPage.maxNumberOfUsers')}
                                            name="maxNumberOfUsers"
                                            value={userConfigState.maxNumberOfUsers || defaultUserConfig?.maxNumberOfUsers}
                                            onChange={handleUserConfigChange}
                                            errors={errors.maxNumberOfUsers}
                                            min={1}
                                        />

                                        <FormInput
                                            type="number"
                                            label={t('registerPage.maxGptUsage')}
                                            name="maxGptUsage"
                                            value={userConfigState.maxGptUsage}
                                            onChange={handleUserConfigChange}
                                            errors={errors.maxGptUsage}
                                            min={0}
                                        />
                                    </>
                                )}
                            </>
                        )}
                </form>
            )}
        </div>
    );
}
